import React from 'react';

export default function JobStagingPhotos({ 
  job, 
  handlePhotoClick, 
  isPhotoViewerOpen, 
  currentPhotoIndex, 
  setIsPhotoViewerOpen, 
  handlePrevPhoto, 
  handleNextPhoto, 
  handleTouchStart, 
  handleTouchMove, 
  handleTouchEnd 
}) {
  return (
    <>
      {/* Property Staging Photos Section */}
      {job?.property && (
        <div className="bg-white shadow rounded-lg mb-3 lg:mb-6">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4 flex items-center">
              <svg className="h-6 w-6 text-indigo-500 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Property Staging Photos
            </h3>

            {job.property.stagingPhotos && job.property.stagingPhotos.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {job.property.stagingPhotos.map((photo, index) => (
                  <div key={index} className="relative group">
                    <div
                      onClick={() => handlePhotoClick(index)}
                      className="cursor-pointer"
                    >
                      <img
                        src={photo}
                        alt={`Staging photo ${index + 1}`}
                        className="h-48 w-full object-cover rounded-lg shadow-sm hover:shadow-md transition-shadow"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-10 transition-opacity rounded-lg flex items-center justify-center">
                        <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity text-sm font-medium flex items-center">
                          <svg className="w-5 h-5 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                          View
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-gray-50 rounded-lg p-8 text-center">
                <svg className="mx-auto h-12 w-12 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p className="mt-2 text-sm text-gray-500">
                  No staging photos available for this property.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Photo Viewer Modal */}
      {isPhotoViewerOpen && job?.property?.stagingPhotos?.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="max-w-7xl w-full h-full flex flex-col items-center justify-center relative">
            <button
              className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
              onClick={() => setIsPhotoViewerOpen(false)}
              aria-label="Close photo viewer"
            >
              <svg className="h-8 w-8" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <div className="flex items-center justify-center w-full h-full px-4">
              <button
                className="absolute left-4 lg:left-8 text-white hover:text-gray-300 focus:outline-none z-10 hidden sm:block"
                onClick={handlePrevPhoto}
                aria-label="Previous photo"
              >
                <svg className="h-12 w-12" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              <div 
                className="relative w-full max-h-full flex items-center justify-center"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                {job?.property?.stagingPhotos?.[currentPhotoIndex] && (
                  <img
                    src={job.property.stagingPhotos[currentPhotoIndex]}
                    alt={`Photo ${currentPhotoIndex + 1}`}
                    className="max-h-[80vh] max-w-full object-contain"
                  />
                )}
              </div>
              
              <button
                className="absolute right-4 lg:right-8 text-white hover:text-gray-300 focus:outline-none z-10 hidden sm:block"
                onClick={handleNextPhoto}
                aria-label="Next photo"
              >
                <svg className="h-12 w-12" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
            
            <div className="absolute bottom-6 left-0 right-0 text-center text-white flex justify-center items-center space-x-4">
              <button
                className="text-white hover:text-gray-300 focus:outline-none sm:hidden"
                onClick={handlePrevPhoto}
                aria-label="Previous photo"
              >
                <svg className="h-8 w-8" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              <p className="text-sm font-medium">
                {currentPhotoIndex + 1} / {job?.property?.stagingPhotos?.length || 0}
              </p>
              
              <button
                className="text-white hover:text-gray-300 focus:outline-none sm:hidden"
                onClick={handleNextPhoto}
                aria-label="Next photo"
              >
                <svg className="h-8 w-8" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
